/**
 * Performance Monitoring Utility
 *
 * Tracks and reports performance metrics for the application
 */

import { logger } from '@/config/env'

interface PerformanceMetric {
  name: string
  value: number
  timestamp: number
  metadata?: Record<string, any>
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = []
  private marks: Map<string, number> = new Map()

  /**
   * Start a performance measurement
   */
  start(name: string) {
    this.marks.set(name, performance.now())
  }

  /**
   * End a performance measurement and record the metric
   */
  end(name: string, metadata?: Record<string, any>) {
    const startTime = this.marks.get(name)
    if (!startTime) {
      logger.warn(`Performance mark not found: ${name}`)
      return
    }

    const duration = performance.now() - startTime
    this.recordMetric(name, duration, metadata)
    this.marks.delete(name)

    return duration
  }

  /**
   * Record a performance metric
   */
  recordMetric(name: string, value: number, metadata?: Record<string, any>) {
    const metric: PerformanceMetric = {
      name,
      value,
      timestamp: Date.now(),
      metadata,
    }

    this.metrics.push(metric)

    // Log in development
    logger.debug(`Performance: ${name} = ${value.toFixed(2)}ms`, metadata)

    // Keep only last 100 metrics to prevent memory leaks
    if (this.metrics.length > 100) {
      this.metrics.shift()
    }
  }

  /**
   * Measure an async function
   */
  async measure<T>(
    name: string,
    fn: () => Promise<T>,
    metadata?: Record<string, any>
  ): Promise<T> {
    this.start(name)
    try {
      const result = await fn()
      this.end(name, { ...metadata, success: true })
      return result
    } catch (error) {
      this.end(name, { ...metadata, success: false, error: String(error) })
      throw error
    }
  }

  /**
   * Measure a synchronous function
   */
  measureSync<T>(
    name: string,
    fn: () => T,
    metadata?: Record<string, any>
  ): T {
    this.start(name)
    try {
      const result = fn()
      this.end(name, { ...metadata, success: true })
      return result
    } catch (error) {
      this.end(name, { ...metadata, success: false, error: String(error) })
      throw error
    }
  }

  /**
   * Get all recorded metrics
   */
  getMetrics(): PerformanceMetric[] {
    return [...this.metrics]
  }

  /**
   * Get metrics by name
   */
  getMetricsByName(name: string): PerformanceMetric[] {
    return this.metrics.filter(m => m.name === name)
  }

  /**
   * Get average value for a metric
   */
  getAverage(name: string): number {
    const metrics = this.getMetricsByName(name)
    if (metrics.length === 0) return 0

    const sum = metrics.reduce((acc, m) => acc + m.value, 0)
    return sum / metrics.length
  }

  /**
   * Get percentile for a metric
   */
  getPercentile(name: string, percentile: number): number {
    const metrics = this.getMetricsByName(name)
    if (metrics.length === 0) return 0

    const sorted = metrics.map(m => m.value).sort((a, b) => a - b)
    const index = Math.ceil((percentile / 100) * sorted.length) - 1
    return sorted[index] || 0
  }

  /**
   * Clear all metrics
   */
  clear() {
    this.metrics = []
    this.marks.clear()
  }

  /**
   * Report performance summary
   */
  report() {
    const summary: Record<string, any> = {}

    const uniqueMetrics = [...new Set(this.metrics.map(m => m.name))]

    uniqueMetrics.forEach(name => {
      const metrics = this.getMetricsByName(name)
      if (metrics.length === 0) return

      summary[name] = {
        count: metrics.length,
        avg: this.getAverage(name).toFixed(2),
        p50: this.getPercentile(name, 50).toFixed(2),
        p95: this.getPercentile(name, 95).toFixed(2),
        p99: this.getPercentile(name, 99).toFixed(2),
      }
    })

    console.table(summary)
    return summary
  }
}

// Global instance
export const performanceMonitor = new PerformanceMonitor()

/**
 * Monitor Web Vitals (Core Web Vitals)
 */
export function monitorWebVitals() {
  // Check if PerformanceObserver is supported
  if (typeof PerformanceObserver === 'undefined') {
    return
  }

  try {
    // Largest Contentful Paint (LCP)
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries()
      const lastEntry = entries[entries.length - 1]
      performanceMonitor.recordMetric('web-vitals.lcp', lastEntry.startTime, {
        type: 'largest-contentful-paint',
      })
    })
    lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] })

    // First Input Delay (FID)
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries()
      entries.forEach((entry: any) => {
        performanceMonitor.recordMetric('web-vitals.fid', entry.processingStart - entry.startTime, {
          type: 'first-input-delay',
        })
      })
    })
    fidObserver.observe({ entryTypes: ['first-input'] })

    // Cumulative Layout Shift (CLS)
    let clsValue = 0
    const clsObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries()
      entries.forEach((entry: any) => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value
          performanceMonitor.recordMetric('web-vitals.cls', clsValue, {
            type: 'cumulative-layout-shift',
          })
        }
      })
    })
    clsObserver.observe({ entryTypes: ['layout-shift'] })

    logger.info('Web Vitals monitoring enabled')
  } catch (error) {
    logger.warn('Failed to setup Web Vitals monitoring:', error)
  }
}

/**
 * Monitor navigation timing
 */
export function monitorNavigationTiming() {
  if (typeof performance === 'undefined' || !performance.timing) {
    return
  }

  window.addEventListener('load', () => {
    setTimeout(() => {
      const timing = performance.timing
      const loadTime = timing.loadEventEnd - timing.navigationStart
      const domReadyTime = timing.domContentLoadedEventEnd - timing.navigationStart
      const responseTime = timing.responseEnd - timing.requestStart

      performanceMonitor.recordMetric('navigation.load-time', loadTime)
      performanceMonitor.recordMetric('navigation.dom-ready-time', domReadyTime)
      performanceMonitor.recordMetric('navigation.response-time', responseTime)

      logger.info('Navigation timing recorded:', {
        loadTime: `${loadTime}ms`,
        domReadyTime: `${domReadyTime}ms`,
        responseTime: `${responseTime}ms`,
      })
    }, 0)
  })
}

/**
 * Monitor React component render time
 */
export function measureRender(componentName: string) {
  return {
    onRenderStart: () => performanceMonitor.start(`render.${componentName}`),
    onRenderEnd: () => performanceMonitor.end(`render.${componentName}`),
  }
}

/**
 * Hook for measuring component render time
 */
export function usePerformanceMonitor(componentName: string) {
  performanceMonitor.start(`render.${componentName}`)

  return () => {
    performanceMonitor.end(`render.${componentName}`)
  }
}

/**
 * Measure API call performance
 */
export async function measureApiCall<T>(
  endpoint: string,
  fn: () => Promise<T>
): Promise<T> {
  return performanceMonitor.measure(`api.${endpoint}`, fn, { endpoint })
}

/**
 * Report slow operations
 */
export function reportSlowOperation(name: string, duration: number, threshold: number = 1000) {
  if (duration > threshold) {
    logger.warn(`Slow operation detected: ${name} took ${duration.toFixed(2)}ms`, {
      name,
      duration,
      threshold,
    })
  }
}

/**
 * Initialize performance monitoring
 */
export function initPerformanceMonitoring() {
  monitorWebVitals()
  monitorNavigationTiming()

  // Report performance metrics every 60 seconds in development
  if (import.meta.env.DEV) {
    setInterval(() => {
      const metrics = performanceMonitor.getMetrics()
      if (metrics.length > 0) {
        logger.debug('Performance Report:')
        performanceMonitor.report()
      }
    }, 60000)
  }

  logger.info('Performance monitoring initialized')
}

export default performanceMonitor
